import {
  DndContext,
  type DragEndEvent,
  type DragStartEvent,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Button,
  Checkbox,
  TableRow as CommonTableRow,
  Icon,
  Table as MaterialTable,
  TableBody,
  TableCell,
  TableHead,
  Typography,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import debounce from 'lodash/debounce';
import {
  useCallback,
  type ReactNode,
  useMemo,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type CSSProperties,
  type UIEvent,
  useRef,
  useEffect,
} from 'react';

import { useTheme } from '../../utils/theme';
import { DelayedLoadingIndicator } from '../DelayedLoadingIndicator';
import { SearchField } from '../SearchField';

// Percentage of scroll to trigger onScrollEnd
const SCROLL_END_THRESHOLD = 95;

const useStyles = makeStyles<Theme, StyleProps>(theme => ({
  searchContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '10px',
  },
  tableContainer: {
    maxHeight: props => props.maxHeight,
    minHeight: props => props.minHeight,
    overflowY: 'auto',
  },
  table: {
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    overflowY: 'auto',
    height: '100%',
  },
  tableHead: {
    borderRadius: theme.shape.borderRadius,
    boxShadow: `0px 0px 0px 1px ${theme.palette.divider} inset`,
    backgroundColor: theme.palette.secondary.background,
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },
  tableHeadCell: {
    fontWeight: 700,
    width: '200px',
  },
  tableRow: {
    backgroundColor: theme.palette.background.default,
    boxShadow:
      `-1px 0px 0px 0px ${theme.palette.divider} inset,` +
      `1px 0px 0px 0px ${theme.palette.divider} inset`,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      cursor: 'pointer',
    },
  },
  tableRowSelected: {
    transition: '0.2s',
    backgroundColor: theme.palette.action.selected,
    '&:hover': {
      backgroundColor: theme.palette.action.selectedOpacity,
    },
  },
  button: {
    height: '56px',
    marginLeft: '10px',
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.error.main,
      transform: 'scale(1.02)',
    },
  },
  buttonIcon: { marginRight: '10px' },
}));

interface StyleProps {
  maxHeight: string;
  minHeight: string;
}

interface ColumnInterface<T> {
  title: string;
  accessor: string | ((row: T) => string | ReactNode);
}

interface DragInterface<T> {
  onSortEnd: (
    draggedIndex: number,
    targetIndex: number,
    draggedRow: T,
    targetRow: T,
  ) => void;
  useHandle: boolean;
  isRowDisabled?: (row: T) => boolean;
}

const TableRow = <T extends { id: string }>({
  onClick,
  className,
  row,
  columns,
  drag,
  rowRef,
  onFocus,
  onBlur,
  onKeyDown,
  onSelectRow,
  selectedRows,
}: {
  onClick?: (event: MouseEvent) => void;
  className?: string;
  row: T;
  columns: ColumnInterface<T>[];
  drag?: DragInterface<T>;
  rowRef: (el: HTMLTableRowElement | null) => void;
  tabIndex?: number;
  onFocus?: () => void;
  onBlur?: () => void;
  onKeyDown?: (event: KeyboardEvent) => void;
  onSelectRow?: (rows: T) => void;
  selectedRows?: T[];
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: row.id,
      disabled:
        !drag || (drag && drag.isRowDisabled && drag?.isRowDisabled(row)),
    });

  const hasCheckbox = onSelectRow !== undefined;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || undefined,
  };

  const draggableProps = { ...attributes, ...listeners };
  const extraRowProps = drag && !drag.useHandle ? draggableProps : {};

  return (
    <CommonTableRow
      {...extraRowProps}
      onClick={onClick}
      className={className}
      style={style}
      ref={el => {
        rowRef(el);
        setNodeRef(el);
      }}
      tabIndex={0}
      onFocus={onFocus}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
    >
      {hasCheckbox && (
        <TableCell
          key="checkbox"
          style={{ width: '3rem', textAlign: 'center' }}
        >
          <Checkbox
            checked={selectedRows?.some(
              selectedRow => selectedRow.id === row.id,
            )}
            onChange={event => {
              event.preventDefault();
              event.stopPropagation();
              if (onSelectRow) onSelectRow(row);
            }}
          />
        </TableCell>
      )}
      {columns.map((column, index) => (
        <TableCell key={row.id + column.title}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {index === 0 && drag && drag.useHandle ? (
              <Icon
                {...draggableProps}
                style={{ cursor: 'grab', marginRight: '10px' }}
                onClick={event => event.stopPropagation()}
              >
                drag_indicator
              </Icon>
            ) : null}
            {typeof column.accessor === 'string'
              ? (row as any)[column.accessor]
              : column.accessor(row)}
          </div>
        </TableCell>
      ))}
    </CommonTableRow>
  );
};

interface Props<T> {
  onScrollEnd?: () => void;
  onSearch?: (searchTerm: string) => void;
  isSelected?: (row: T) => boolean;
  columns: {
    title: string;
    titleComponent?: ReactNode;
    accessor: string | ((row: T) => string | ReactNode);
    width?: string;
    align?: 'left' | 'right' | 'center';
  }[];
  rows: T[];
  onRowClick: (row: T) => void;
  onRowFocus?: (row: T) => void;
  searchFilter?: (row: T, searchTerm: string) => boolean;
  searchFilterPlaceholder?: string;
  drag?: DragInterface<T>;
  noMatchPlaceholder?: string;
  maxHeight?: string;
  style?: CSSProperties;
  selectedRows?: T[];
  onSelectRows?: (rows: T[]) => void;
  withoutHeader?: boolean;
  isLoading?: boolean;
  minHeight?: string;
}

export const ComplexTable = <T extends { id: string }>({
  onScrollEnd = () => {},
  onSearch,
  isSelected,
  columns,
  rows,
  onRowClick = () => {},
  onRowFocus = () => {},
  searchFilter,
  searchFilterPlaceholder,
  drag,
  noMatchPlaceholder = 'No matches found, try clearing your filter',
  maxHeight = '525px',
  style,
  selectedRows,
  onSelectRows,
  withoutHeader = false,
  isLoading = false,
  minHeight = 'auto',
}: Props<T>) => {
  const classes = useStyles({ maxHeight, minHeight });
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();

  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor));
  const hasCheckbox = onSelectRows !== undefined;

  const localSelectedRows = useMemo(() => selectedRows ?? [], [selectedRows]);

  const [activeId, setActiveId] = useState<string | null>(null);
  const items = useMemo(() => rows?.map(({ id }) => id), [rows]);

  const [searchTerm, setSearchTerm] = useState('');
  const rowsRef = useRef<HTMLTableRowElement[]>([]);

  const assignRef = (element: HTMLTableRowElement | null, index: number) => {
    if (element) rowsRef.current[index] = element;
  };

  const filteredRows =
    searchFilter && !onSearch
      ? rows.filter(row => searchFilter(row, searchTerm))
      : rows;

  const handleDragCancel = useCallback(() => {
    setActiveId(null);
  }, []);

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      if (!drag) return;
      setActiveId(event.active.id as string);
    },
    [drag],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const targetIndex = rows.findIndex(row => row.id === event.over?.id);
      const draggedIndex = rows.findIndex(row => row.id === activeId);
      const targetRow = rows[targetIndex];
      const draggedRow = rows[draggedIndex];

      if (!targetRow || !draggedRow) return;
      drag?.onSortEnd(draggedIndex, targetIndex, draggedRow, targetRow);
      setActiveId(null);
    },
    [activeId, drag, rows],
  );

  useEffect(() => {
    if (drag) {
      // Check if there are rows with same ID
      const ids = rows.map(row => row.id);
      const hasDuplicate = new Set(ids).size !== ids.length;
      if (hasDuplicate) {
        console.warn(
          'Duplicate row IDs found, ' +
            'drag and drop will not work correctly.',
        );
      }
    }
  }, [drag, rows]);

  const selectRow = useCallback(
    (rowToSelect: T) => {
      if (hasCheckbox) {
        const isRowSelected = localSelectedRows.some(
          selectedRow => selectedRow.id === rowToSelect.id,
        );
        if (isRowSelected) {
          onSelectRows(
            localSelectedRows.filter(
              selectedRow => selectedRow.id !== rowToSelect.id,
            ),
          );
        } else {
          onSelectRows([...localSelectedRows, rowToSelect]);
        }
      }
    },
    [hasCheckbox, localSelectedRows, onSelectRows],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedOnSearch = useCallback(
    debounce(search => {
      if (onSearch) {
        scrollContainerRef.current?.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
        onSearch(search);
      }
    }, 300),
    [onSearch],
  );

  const doSearch = useCallback(
    (search: string) => {
      debouncedOnSearch(search);
      setSearchTerm(search);
    },
    [debouncedOnSearch],
  );

  const renderMaterialTable = () => (
    <MaterialTable className={classes.table}>
      {!withoutHeader && (
        <TableHead className={classes.tableHead}>
          {hasCheckbox && (
            <TableCell
              key="checkbox"
              className={classes.tableHeadCell}
              style={{ width: '3rem', textAlign: 'center' }}
            >
              <Checkbox
                checked={
                  localSelectedRows?.length === rows.length && !!rows?.length
                }
                indeterminate={
                  localSelectedRows?.length > 0 &&
                  localSelectedRows?.length < rows.length
                }
                onChange={() => {
                  if (localSelectedRows?.length === rows.length) {
                    onSelectRows([]);
                  } else {
                    onSelectRows(filteredRows);
                  }
                }}
              />
            </TableCell>
          )}
          {columns.map(column => (
            <TableCell
              key={column.title}
              className={classes.tableHeadCell}
              style={{ width: column.width, textAlign: column.align }}
            >
              {column.titleComponent || column.title}
            </TableCell>
          ))}
        </TableHead>
      )}
      {isLoading ? (
        <TableBody>
          <DelayedLoadingIndicator />
        </TableBody>
      ) : (
        <TableBody
          sx={{
            borderTop: withoutHeader
              ? `1px solid ${theme.palette.divider}`
              : undefined,
          }}
        >
          {filteredRows.length === 0 ? (
            <CommonTableRow className={classes.tableRow}>
              {/* There's an extra column for the checkbox so we add 1 */}
              <TableCell colSpan={columns.length + 1}>
                <Typography color="grey">{noMatchPlaceholder}</Typography>
              </TableCell>
            </CommonTableRow>
          ) : (
            filteredRows.map((row, index) => (
              <TableRow
                key={row.id}
                row={row}
                columns={columns}
                drag={drag}
                rowRef={el => assignRef(el, index)}
                selectedRows={localSelectedRows}
                onSelectRow={hasCheckbox ? selectRow : undefined}
                className={
                  isSelected && isSelected(row)
                    ? `${classes.tableRow} ${classes.tableRowSelected}`
                    : classes.tableRow
                }
                onClick={e => {
                  if (e.ctrlKey || e.metaKey) {
                    selectRow(row);
                    return;
                  }
                  if (
                    e.shiftKey &&
                    localSelectedRows.length > 0 &&
                    hasCheckbox
                  ) {
                    e.preventDefault();
                    e.stopPropagation();
                    const selectedRowIndex = filteredRows.findIndex(
                      selectedRow => selectedRow.id === localSelectedRows[0].id,
                    );
                    const clickedRowIndex = filteredRows.findIndex(
                      clickedRow => clickedRow.id === row.id,
                    );
                    const minIndex = Math.min(
                      selectedRowIndex,
                      clickedRowIndex,
                    );
                    const maxIndex = Math.max(
                      selectedRowIndex,
                      clickedRowIndex,
                    );
                    const rowsToSelect = filteredRows.slice(
                      minIndex,
                      maxIndex + 1,
                    );
                    onSelectRows(rowsToSelect);
                    return;
                  }
                  if (onRowClick) onRowClick(row);
                }}
                onFocus={() => {
                  if (onRowFocus) onRowFocus(row);
                }}
                onKeyDown={handleKeyDownEvent}
              />
            ))
          )}
        </TableBody>
      )}
    </MaterialTable>
  );

  const handleKeyDownEvent = (event: KeyboardEvent) => {
    event.preventDefault();
    if (!isSelected) return;
    const selectedRowIndex = filteredRows.findIndex(row => isSelected(row));
    if (
      event.key === 'ArrowDown' &&
      selectedRowIndex < filteredRows.length - 1
    ) {
      if (onRowClick) {
        onRowClick(filteredRows[selectedRowIndex + 1]);
      }
    } else if (event.key === 'ArrowUp' && selectedRowIndex > 0) {
      if (onRowClick) {
        onRowClick(filteredRows[selectedRowIndex - 1]);
      }
    }
  };

  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    const target = event.target as HTMLDivElement;
    const scrollPercentage =
      (target.scrollTop / (target.scrollHeight - target.clientHeight)) * 100;
    if (scrollPercentage >= SCROLL_END_THRESHOLD) {
      onScrollEnd();
    }
  };

  return (
    <div style={style}>
      {(searchFilter || onSearch) && (
        <div className={classes.searchContainer}>
          <SearchField
            id="search-input"
            value={searchTerm}
            size="medium"
            fullWidth={false}
            placeholder={searchFilterPlaceholder}
            onChange={event => doSearch(event.target.value)}
          />
          {searchTerm && (
            <Button onClick={() => doSearch('')} className={classes.button}>
              <Icon fontSize="small" className={classes.buttonIcon}>
                cancel
              </Icon>
              Clear filter
            </Button>
          )}
        </div>
      )}

      <div
        className={classes.tableContainer}
        onScroll={handleScroll}
        ref={scrollContainerRef}
      >
        {drag ? (
          <DndContext
            sensors={sensors}
            onDragStart={handleDragStart}
            onDragCancel={handleDragCancel}
            onDragEnd={handleDragEnd}
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis]}
          >
            <SortableContext
              items={items}
              strategy={verticalListSortingStrategy}
            >
              {renderMaterialTable()}
            </SortableContext>
          </DndContext>
        ) : (
          renderMaterialTable()
        )}
      </div>
    </div>
  );
};
